import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { articles } from '@/mocks/gossip';

export default function GossipDetail() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);

  const article = useMemo(() => articles.find((item) => item.id === id), [id]);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter((item) => item.id !== article.id && item.categorySlug === article.categorySlug);
    const others = articles.filter((item) => item.id !== article.id && item.categorySlug !== article.categorySlug);
    return [...sameCategory, ...others].slice(0, 4);
  }, [article]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <div className="page-enter min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-32 text-center md:px-6">
          <i className="ri-file-unknow-line text-5xl text-foreground-400"></i>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这篇文章走丢了</h1>
          <p className="mt-3 text-sm text-foreground-600">链接可能已失效，或者内容已经被移除。</p>
          <Link
            to="/gossip"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回吃瓜广场
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="page-enter min-h-screen bg-background-50">
      <title>{`${article.title} | 爆瓜福利`}</title>
      <meta name="description" content={article.summary} />
      <meta name="keywords" content={`${article.tags.join(',')},爆瓜福利,吃瓜`} />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: '爆瓜福利' },
          datePublished: article.date,
          articleSection: article.category,
          inLanguage: 'zh-CN',
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
            { '@type': 'ListItem', position: 2, name: '吃瓜广场', item: '/gossip' },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />

      <SiteHeader />
      <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '吃瓜广场', to: '/gossip' },
            { label: article.category, to: `/gossip?category=${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <header>
              <Link
                to={`/gossip?category=${article.categorySlug}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line"></i>
                {article.category}
              </Link>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl md:leading-tight">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString('zh-CN')} 次阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-timer-line"></i>
                  阅读约 {article.readTime}
                </span>
                <button
                  type="button"
                  onClick={copyLink}
                  className="ml-auto flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={copied ? 'ri-check-line text-accent-600' : 'ri-link'}></i>
                  {copied ? '链接已复制' : '复制链接'}
                </button>
              </div>
            </header>

            <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-96">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 爆瓜福利`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8">
              <p className="rounded-lg border-l-4 border-primary-500 bg-background-100 p-5 text-sm leading-7 text-foreground-700">
                {article.summary}
              </p>
            </div>

            <div className="mt-8 space-y-9">
              {article.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{section.heading}</h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-8 text-foreground-700 md:text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-xs text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/gossip?q=${encodeURIComponent(tag)}`}
                  className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-fire-line text-primary-600"></i>
                  相关推荐
                </h2>
                <div className="mt-2">
                  {related.map((item) => (
                    <div key={item.id} className="border-b border-background-200 py-4 last:border-b-0">
                      <Link to={`/gossip/${item.id}`} className="group block">
                        <h3 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </h3>
                        <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                          <span className="rounded bg-background-200 px-2 py-0.5">{item.category}</span>
                          <span>{item.date}</span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-group-line text-primary-600"></i>
                  加入分享社区
                </h2>
                <p className="mt-3 text-xs leading-6 text-foreground-600">
                  想第一时间看到更多爆料？加入爆瓜福利分享群，或领取站内分享码，获取每日更新的吃瓜合集。
                </p>
                <Link
                  to="/community"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看分享社区
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14 border-t border-background-200 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续吃瓜</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/gossip/${item.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-32 w-full overflow-hidden">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 爆瓜福利`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-700">
                    {item.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}